(() => {
  if (window.freeGiftCartController) return;

  const FREE_GIFT_DISCOUNT_TITLE = 'free gift from buy nmn get free tmg';
  const CART_ROW_SELECTOR = '.cart-item';
  const RESTRICTED_CONTROL_SELECTOR = [
    '.cart-item__quantity button',
    '.cart-item__quantity input',
    '.cart-item__quantity select',
    '.cart-item__quantity cart-remove-button',
    '.cart-item__subscription button',
    '.cart-item__subscription input',
    '.cart-item__subscription select',
    '.subscription-ticker button',
    '.subscription-ticker input',
    '.subscription-ticker select',
    'subscribe-selector button',
    'subscribe-selector input',
    'subscribe-selector select',
  ].join(',');

  class FreeGiftCartController {
    constructor() {
      this.drawer = null;
      this.giftKeys = new Set();
      this.syncScheduled = false;
      this.syncInFlight = false;
      this.syncPending = false;
      this.refreshInFlight = false;
      this.lastCanonicalRefreshSignature = null;

      this.handleInteraction = this.handleInteraction.bind(this);
      this.handleDrawerMutations = this.handleDrawerMutations.bind(this);
      this.handleRootMutations = this.handleRootMutations.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);

      this.drawerObserver = new MutationObserver(this.handleDrawerMutations);
      this.rootObserver = new MutationObserver(this.handleRootMutations);

      this.start();
    }

    start() {
      ['click', 'change', 'input', 'keydown'].forEach((eventName) => {
        document.addEventListener(eventName, this.handleInteraction, true);
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleSync());
      }

      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      this.rootObserver.observe(document.body, { childList: true, subtree: true });
      this.connectDrawer(document.querySelector('cart-drawer'));
      this.scheduleSync();
    }

    connectDrawer(drawer) {
      if (drawer === this.drawer) return;

      this.drawerObserver.disconnect();
      this.drawer = drawer;

      if (!this.drawer) return;

      this.drawerObserver.observe(this.drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });
      this.scheduleSync();
    }

    handleSectionLoad(event) {
      const sectionDrawer = event.target instanceof Element
        ? event.target.matches('cart-drawer')
          ? event.target
          : event.target.querySelector('cart-drawer')
        : null;

      this.connectDrawer(sectionDrawer || document.querySelector('cart-drawer'));
      this.scheduleSync();
    }

    handleRootMutations(mutations) {
      let replacementDrawer = null;

      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches('cart-drawer')) {
            replacementDrawer = node;
            break;
          }

          const nestedDrawer = node.querySelector('cart-drawer');
          if (nestedDrawer) {
            replacementDrawer = nestedDrawer;
            break;
          }
        }

        if (replacementDrawer) break;
      }

      if (replacementDrawer || (this.drawer && !this.drawer.isConnected)) {
        this.connectDrawer(replacementDrawer || document.querySelector('cart-drawer'));
      }
    }

    handleDrawerMutations(mutations) {
      let shouldApplyKnownState = false;
      let shouldSync = false;

      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          if (mutation.target === this.drawer) shouldSync = true;
          continue;
        }

        const targetRow = mutation.target instanceof Element
          ? mutation.target.closest(CART_ROW_SELECTOR)
          : null;

        if (targetRow) {
          shouldSync = true;
          if (this.isKnownGiftRow(targetRow)) shouldApplyKnownState = true;
        }

        for (const node of [...mutation.addedNodes, ...mutation.removedNodes]) {
          if (!(node instanceof Element)) continue;

          if (
            node.matches('cart-drawer-items, .cart-item') ||
            node.querySelector('cart-drawer-items, .cart-item')
          ) {
            shouldSync = true;
          }

          if (
            node.matches('.cart-item__subscription, .subscription-ticker, subscribe-selector, quantity-popover') ||
            node.querySelector('.cart-item__subscription, .subscription-ticker, subscribe-selector, quantity-popover')
          ) {
            shouldApplyKnownState = true;
          }
        }
      }

      if (shouldApplyKnownState) this.applyGiftStateToRows();
      if (shouldSync) this.scheduleSync();
    }

    handleInteraction(event) {
      if (!(event.target instanceof Element)) return;

      const row = event.target.closest(CART_ROW_SELECTOR);
      if (!row || !event.target.closest(RESTRICTED_CONTROL_SELECTOR)) return;
      if (!this.isKnownGiftRow(row)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    isKnownGiftRow(row) {
      const lineKey = row.dataset.cartLineKey;
      return row.dataset.freeGift === 'true' || Boolean(lineKey && this.giftKeys.has(lineKey));
    }

    scheduleSync() {
      if (this.syncScheduled) return;

      this.syncScheduled = true;
      queueMicrotask(() => {
        this.syncScheduled = false;
        this.syncCartState();
      });
    }

    async syncCartState() {
      if (this.syncInFlight) {
        this.syncPending = true;
        return;
      }

      this.syncInFlight = true;

      try {
        const response = await fetch(this.cartJsonUrl(), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);

        const cart = await response.json();
        const giftItems = (cart.items || []).filter((item) => this.isFreeGiftItem(item));
        const needsCanonicalRefresh = this.requiresCanonicalRefresh(giftItems);

        this.giftKeys = new Set(giftItems.map((item) => item.key).filter(Boolean));
        this.applyGiftStateToRows();

        if (needsCanonicalRefresh) {
          await this.refreshCanonicalDrawer(this.cartSignature(cart));
        } else {
          this.lastCanonicalRefreshSignature = null;
        }
      } catch (error) {
        console.error('Unable to synchronize free-gift cart state.', error);
      } finally {
        this.syncInFlight = false;

        if (this.syncPending) {
          this.syncPending = false;
          this.scheduleSync();
        }
      }
    }

    isFreeGiftItem(item) {
      const properties = item.properties || {};
      const hasDiscountyClaim = properties._claim_type === 'discounty-auto';
      const discountTitles = [
        ...(item.discounts || []).map((discount) => discount.title),
        ...(item.line_level_discount_allocations || []).map(
          (allocation) => allocation.discount_application && allocation.discount_application.title
        ),
      ]
        .filter(Boolean)
        .map((title) => String(title).trim().toLowerCase());

      const hasGiftDiscount = hasDiscountyClaim
        ? discountTitles.some((title) => title.includes('free gift'))
        : discountTitles.includes(FREE_GIFT_DISCOUNT_TITLE);
      const originalLinePrice = Number(item.original_line_price);
      const finalLinePrice = Number(item.final_line_price);

      return hasGiftDiscount && originalLinePrice > 0 && finalLinePrice === 0;
    }

    requiresCanonicalRefresh(giftItems) {
      if (!this.drawer) return false;

      const rows = this.cartRows();
      const rowKeys = new Set(rows.map((row) => row.dataset.cartLineKey).filter(Boolean));
      const missingGiftKey = giftItems.some((item) => item.key && !rowKeys.has(item.key));
      const staleGiftMarker = rows.some((row) => {
        const lineKey = row.dataset.cartLineKey;
        return row.dataset.freeGift === 'true' && (!lineKey || !giftItems.some((item) => item.key === lineKey));
      });

      return missingGiftKey || staleGiftMarker;
    }

    applyGiftStateToRows() {
      for (const row of this.cartRows()) {
        const lineKey = row.dataset.cartLineKey;
        const isFreeGift = Boolean(lineKey && this.giftKeys.has(lineKey));

        row.classList.toggle('cart-item--free-gift', isFreeGift);
        row.dataset.freeGift = isFreeGift ? 'true' : 'false';

        if (isFreeGift) {
          this.restrictGiftRow(row);
        } else if (row.dataset.freeGiftJsManaged === 'true') {
          this.restoreRow(row);
        }
      }
    }

    restrictGiftRow(row) {
      row.dataset.freeGiftJsManaged = 'true';

      row
        .querySelectorAll('.cart-item__subscription, .subscription-ticker, subscribe-selector, quantity-popover, cart-remove-button')
        .forEach((element) => this.hideElement(element));

      row.querySelectorAll(RESTRICTED_CONTROL_SELECTOR).forEach((control) => {
        this.disableControl(control);
      });

      const quantityInput = row.querySelector('.cart-item__quantity input');
      if (quantityInput) quantityInput.value = '1';

      if (!row.querySelector('.cart-item__quantity-static')) {
        const quantityCell = row.querySelector('.cart-item__quantity');
        if (!quantityCell) return;

        const wrapper = document.createElement('div');
        const staticQuantity = document.createElement('span');
        const inputLabel = quantityInput && quantityInput.getAttribute('aria-label');

        wrapper.className = 'cart-item__quantity-wrapper cart-item__quantity-wrapper--free-gift';
        wrapper.dataset.freeGiftFallback = 'true';
        staticQuantity.className = 'cart-item__quantity-static';
        staticQuantity.textContent = '1';

        if (inputLabel) staticQuantity.setAttribute('aria-label', `${inputLabel}: 1`);

        wrapper.append(staticQuantity);
        quantityCell.append(wrapper);
      }
    }

    hideElement(element) {
      if (element.dataset.freeGiftHidden !== 'true') {
        element.dataset.freeGiftHidden = 'true';
        element.dataset.freeGiftWasHidden = element.hidden ? 'true' : 'false';
      }

      element.hidden = true;
    }

    disableControl(control) {
      if (!('disabled' in control)) return;

      if (control.dataset.freeGiftDisabled !== 'true') {
        control.dataset.freeGiftDisabled = 'true';
        control.dataset.freeGiftWasDisabled = control.disabled ? 'true' : 'false';
      }

      control.disabled = true;

      if ('readOnly' in control) {
        if (!control.dataset.freeGiftWasReadOnly) {
          control.dataset.freeGiftWasReadOnly = control.readOnly ? 'true' : 'false';
        }
        control.readOnly = true;
      }
    }

    restoreRow(row) {
      row.querySelectorAll('[data-free-gift-hidden="true"]').forEach((element) => {
        element.hidden = element.dataset.freeGiftWasHidden === 'true';
        delete element.dataset.freeGiftHidden;
        delete element.dataset.freeGiftWasHidden;
      });

      row.querySelectorAll('[data-free-gift-disabled="true"]').forEach((control) => {
        control.disabled = control.dataset.freeGiftWasDisabled === 'true';
        if ('readOnly' in control) control.readOnly = control.dataset.freeGiftWasReadOnly === 'true';
        delete control.dataset.freeGiftDisabled;
        delete control.dataset.freeGiftWasDisabled;
        delete control.dataset.freeGiftWasReadOnly;
      });

      row.querySelectorAll('[data-free-gift-fallback="true"]').forEach((element) => element.remove());
      delete row.dataset.freeGiftJsManaged;
    }

    async refreshCanonicalDrawer(signature) {
      if (
        this.refreshInFlight ||
        (this.lastCanonicalRefreshSignature && this.lastCanonicalRefreshSignature === signature)
      ) {
        return;
      }

      this.refreshInFlight = true;
      this.lastCanonicalRefreshSignature = signature;

      try {
        const response = await fetch(this.cartSectionUrl(), {
          cache: 'no-store',
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) throw new Error(`Cart section request failed with ${response.status}`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const sourceItems = parsedDocument.querySelector('cart-drawer-items');
        const sourceFooter = parsedDocument.querySelector('.cart-drawer__footer');
        const currentItems = document.querySelector('cart-drawer-items');
        const currentFooter = document.querySelector('.cart-drawer__footer');

        if (sourceItems && currentItems) currentItems.replaceWith(sourceItems);
        if (sourceFooter && currentFooter) currentFooter.replaceWith(sourceFooter);

        this.connectDrawer(document.querySelector('cart-drawer'));
        this.applyGiftStateToRows();

        if (this.hasAllGiftKeysInDrawer()) this.lastCanonicalRefreshSignature = null;
      } catch (error) {
        this.lastCanonicalRefreshSignature = null;
        console.error('Unable to refresh canonical cart drawer markup.', error);
      } finally {
        this.refreshInFlight = false;
      }
    }

    hasAllGiftKeysInDrawer() {
      const rowKeys = new Set(this.cartRows().map((row) => row.dataset.cartLineKey).filter(Boolean));
      return [...this.giftKeys].every((key) => rowKeys.has(key));
    }

    cartRows() {
      return this.drawer ? [...this.drawer.querySelectorAll(CART_ROW_SELECTOR)] : [];
    }

    cartSignature(cart) {
      return (cart.items || [])
        .map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`)
        .join('|');
    }

    shopRoot() {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      return root || '/';
    }

    cartJsonUrl() {
      return `${this.shopRoot()}cart.js`;
    }

    cartSectionUrl() {
      return `${this.shopRoot()}cart?section_id=cart-drawer`;
    }
  }

  window.freeGiftCartController = new FreeGiftCartController();
})();
